import { cn } from "@/lib/util/utils";
import { Typography } from "@/modules/common/components/typography";
import { CountryLink } from "@/modules/layout/components/countryLink";
import { ComponentPropsWithoutRef, ReactNode } from "react";

interface DocumentProps extends ComponentPropsWithoutRef<"section"> {
  children: ReactNode;
}

export default function PolicyDocument({
  children,
  className,
  ...props
}: DocumentProps) {
  return (
    <article className={cn("flex flex-col gap-10", className)} {...props}>
      {children}
    </article>
  );
}

interface HeaderTitleProps extends Omit<DocumentProps, "children"> {
  title: string;
  subtitle?: string;
}

function HeaderTitle({
  title = "",
  subtitle,
  className,
  ...props
}: HeaderTitleProps) {
  return (
    <header
      className={cn(
        "group flex flex-col gap-1 border-gray-300 border-b",
        className
      )}
      {...props}
    >
      <Typography variant="h1" className="mb-4" align={"center"}>
        {title}
      </Typography>
      {subtitle && (
        <p className="pb-4 font-semibold text-gray-400 text-xs text-center uppercase tracking-widest">
          {subtitle}
        </p>
      )}
    </header>
  );
}

function DocumentLink({
  className,
  title,
  href,
  ...props
}: Omit<DocumentProps, "children"> & {
  title: string;
  href: string;
}) {
  return (
    <CountryLink href={href}>
      <p
        className={cn(
          "font-semibold text-gray-400 text-xs hover:underline tracking-widest",
          className
        )}
        {...props}
      >
        {title}
      </p>
    </CountryLink>
  );
}

function Section({ children, className, ...props }: DocumentProps) {
  return (
    <section className={cn("group flex flex-col gap-3", className)} {...props}>
      {children}
    </section>
  );
}

function Title({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Typography>) {
  return (
    <Typography
      variant="h2"
      className={cn("group-hover:text-primary transition-colors", className)}
      {...props}
    >
      {children}
    </Typography>
  );
}

function Content({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={cn("text-sm leading-relaxed whitespace-pre-line", className)}
      {...props}
    >
      {children}
    </div>
  );
}

PolicyDocument.Section = Section;
PolicyDocument.Header = HeaderTitle;
PolicyDocument.Title = Title;
PolicyDocument.Content = Content;
PolicyDocument.Link = DocumentLink;
